import React, { useState, useEffect, useCallback } from 'react';
import { useOrg } from '../context/OrgContext';
import { usePermissions } from '../components/PermissionGuard';
import { PageHeader } from '../components/PageHeader';
import * as complianceDocsApi from '../api/complianceDocs';

export function ComplianceDocuments() {
  const { selectedOrg } = useOrg();
  const { canReadAssets, canWriteAssets } = usePermissions();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showVersionsModal, setShowVersionsModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [versions, setVersions] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [filters, setFilters] = useState({
    programId: '',
    siteId: '',
    category: '',
    search: ''
  });
  const [uploadForm, setUploadForm] = useState({
    docKey: '',
    name: '',
    category: 'certification',
    programId: 'program-nevi',
    siteId: '',
    assetId: ''
  });

  const loadDocs = useCallback(async () => {
    if (!selectedOrg || !canReadAssets) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await complianceDocsApi.getComplianceDocs(selectedOrg.id, {
        programId: filters.programId || undefined,
        siteId: filters.siteId || undefined,
        category: filters.category || undefined,
        search: filters.search || undefined
      });
      setDocs(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, canReadAssets, filters]);

  useEffect(() => {
    loadDocs();
  }, [loadDocs]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleOpenUpload = () => {
    setUploadForm({
      docKey: '',
      name: '',
      category: 'certification',
      programId: 'program-nevi',
      siteId: '',
      assetId: ''
    });
    setShowUploadModal(true);
  };

  const handleUpload = async () => {
    if (!selectedOrg || uploading) return;
    setUploading(true);
    try {
      await complianceDocsApi.uploadComplianceDoc(selectedOrg.id, {
        docKey: uploadForm.docKey || `${uploadForm.name.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}`,
        name: uploadForm.name,
        category: uploadForm.category,
        programId: uploadForm.programId,
        siteId: uploadForm.siteId || null,
        assetId: uploadForm.assetId || null,
        mimeType: 'application/pdf',
        fileSize: Math.floor(Math.random() * 1000000) + 10000,
        artifactId: `artifact-${Date.now()}`
      });
      setShowUploadModal(false);
      loadDocs();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleViewVersions = async (doc) => {
    setSelectedDoc(doc);
    try {
      const versionData = await complianceDocsApi.getDocVersions(selectedOrg.id, doc.id);
      setVersions(versionData);
      setShowVersionsModal(true);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDownload = async (doc, version) => {
    if (!selectedOrg) return;
    try {
      await complianceDocsApi.logDocDownload(selectedOrg.id, doc.id, version);
    } catch (err) {
      console.error('Failed to log download:', err);
    }
  };

  if (!canReadAssets) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You do not have permission to view compliance documents.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <PageHeader
        title="Compliance Document Library"
        subtitle="Manage and version compliance documents"
      />

      <div style={styles.toolbar}>
        <div style={styles.filters}>
          <input
            type="text"
            placeholder="Search documents..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            style={styles.searchInput}
            data-testid="doc-search"
          />
          <select
            value={filters.category}
            onChange={(e) => handleFilterChange('category', e.target.value)}
            style={styles.select}
            data-testid="doc-category-filter"
          >
            <option value="">All Categories</option>
            <option value="certification">Certification</option>
            <option value="permit">Permit</option>
            <option value="manual">Manual</option>
            <option value="warranty">Warranty</option>
            <option value="inspection">Inspection</option>
          </select>
          <select
            value={filters.programId}
            onChange={(e) => handleFilterChange('programId', e.target.value)}
            style={styles.select}
            data-testid="doc-program-filter"
          >
            <option value="">All Programs</option>
            <option value="program-nevi">NEVI</option>
            <option value="program-fleet">Fleet</option>
          </select>
        </div>
        {canWriteAssets && (
          <button
            onClick={handleOpenUpload}
            style={styles.uploadButton}
            data-testid="upload-doc-button"
          >
            + Upload Document
          </button>
        )}
      </div>

      {error && (
        <div style={styles.error}>{error}</div>
      )}

      {loading ? (
        <div style={styles.loading}>Loading documents...</div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table} data-testid="docs-table">
            <thead>
              <tr>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Category</th>
                <th style={styles.th}>Program</th>
                <th style={styles.th}>Site</th>
                <th style={styles.th}>Version</th>
                <th style={styles.th}>Updated</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {docs.map(doc => (
                <tr key={doc.id} style={styles.tr} data-testid={`doc-${doc.id}`}>
                  <td style={styles.td}>
                    <div style={styles.docName}>{doc.name}</div>
                    <div style={styles.docKey}>{doc.docKey}</div>
                  </td>
                  <td style={styles.td}>
                    <span style={styles.categoryBadge}>{doc.category}</span>
                  </td>
                  <td style={styles.td}>{doc.programId}</td>
                  <td style={styles.td}>{doc.siteId || '-'}</td>
                  <td style={styles.td}>v{doc.currentVersion}</td>
                  <td style={styles.td}>{new Date(doc.updatedAt).toLocaleDateString()}</td>
                  <td style={styles.td}>
                    <button
                      onClick={() => handleViewVersions(doc)}
                      style={styles.actionButton}
                      data-testid={`versions-${doc.id}`}
                    >
                      Versions
                    </button>
                    <button
                      onClick={() => handleDownload(doc, doc.currentVersion)}
                      style={styles.downloadButton}
                      data-testid={`download-${doc.id}`}
                    >
                      Download
                    </button>
                  </td>
                </tr>
              ))}
              {docs.length === 0 && (
                <tr>
                  <td colSpan="7" style={styles.emptyState}>
                    No documents found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showUploadModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal} data-testid="upload-modal">
            <h3 style={styles.modalTitle}>Upload Document</h3>
            <div style={styles.formGroup}>
              <label style={styles.label}>Document Name</label>
              <input
                type="text"
                value={uploadForm.name}
                onChange={(e) => setUploadForm(prev => ({ ...prev, name: e.target.value }))}
                style={styles.input}
                data-testid="upload-name-input"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Category</label>
              <select
                value={uploadForm.category}
                onChange={(e) => setUploadForm(prev => ({ ...prev, category: e.target.value }))}
                style={styles.select}
                data-testid="upload-category-input"
              >
                <option value="certification">Certification</option>
                <option value="permit">Permit</option>
                <option value="manual">Manual</option>
                <option value="warranty">Warranty</option>
                <option value="inspection">Inspection</option>
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Program</label>
              <select
                value={uploadForm.programId}
                onChange={(e) => setUploadForm(prev => ({ ...prev, programId: e.target.value }))}
                style={styles.select}
                data-testid="upload-program-input"
              >
                <option value="program-nevi">NEVI</option>
                <option value="program-fleet">Fleet</option>
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Site (optional)</label>
              <input
                type="text"
                value={uploadForm.siteId}
                onChange={(e) => setUploadForm(prev => ({ ...prev, siteId: e.target.value }))}
                style={styles.input}
                placeholder="e.g., site-1"
                data-testid="upload-site-input"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Asset (optional)</label>
              <input
                type="text"
                value={uploadForm.assetId}
                onChange={(e) => setUploadForm(prev => ({ ...prev, assetId: e.target.value }))}
                style={styles.input}
                placeholder="e.g., asset-1"
                data-testid="upload-asset-input"
              />
            </div>
            <div style={styles.modalActions}>
              <button
                onClick={() => setShowUploadModal(false)}
                style={styles.cancelButton}
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={uploading || !uploadForm.name}
                style={styles.submitButton}
                data-testid="confirm-upload-button"
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showVersionsModal && selectedDoc && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal} data-testid="versions-modal">
            <h3 style={styles.modalTitle}>Version History: {selectedDoc.name}</h3>
            <table style={styles.versionsTable}>
              <thead>
                <tr>
                  <th style={styles.th}>Version</th>
                  <th style={styles.th}>Uploaded</th>
                  <th style={styles.th}>Size</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {versions.map(ver => (
                  <tr key={ver.id} style={styles.tr}>
                    <td style={styles.td}>v{ver.version}</td>
                    <td style={styles.td}>{new Date(ver.uploadedAt).toLocaleString()}</td>
                    <td style={styles.td}>{(ver.fileSize / 1024).toFixed(1)} KB</td>
                    <td style={styles.td}>
                      <button
                        onClick={() => handleDownload(selectedDoc, ver.version)}
                        style={styles.downloadButton}
                      >
                        Download
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={styles.modalActions}>
              <button
                onClick={() => setShowVersionsModal(false)}
                style={styles.cancelButton}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: '2rem',
    maxWidth: '1400px',
    margin: '0 auto'
  },
  accessDenied: {
    padding: '3rem',
    textAlign: 'center',
    background: '#fff',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
    flexWrap: 'wrap',
    gap: '0.5rem'
  },
  filters: {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap'
  },
  searchInput: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    fontSize: '0.875rem',
    minWidth: '200px'
  },
  select: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    fontSize: '0.875rem',
    background: 'white'
  },
  uploadButton: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #3182ce',
    background: '#3182ce',
    color: 'white',
    fontSize: '0.875rem',
    cursor: 'pointer'
  },
  error: {
    padding: '1rem',
    background: '#fed7d7',
    color: '#c53030',
    borderRadius: '8px',
    marginBottom: '1rem'
  },
  loading: {
    padding: '2rem',
    textAlign: 'center',
    color: '#718096'
  },
  tableContainer: {
    background: 'white',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    overflow: 'auto'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  versionsTable: {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: '1rem'
  },
  th: {
    textAlign: 'left',
    padding: '1rem',
    background: '#f7fafc',
    borderBottom: '1px solid #e2e8f0',
    fontWeight: '600',
    fontSize: '0.875rem',
    color: '#4a5568',
    whiteSpace: 'nowrap'
  },
  tr: {
    borderBottom: '1px solid #e2e8f0'
  },
  td: {
    padding: '1rem',
    fontSize: '0.875rem'
  },
  docName: {
    fontWeight: '500'
  },
  docKey: {
    fontSize: '0.75rem',
    color: '#718096'
  },
  categoryBadge: {
    padding: '0.25rem 0.5rem',
    background: '#e2e8f0',
    borderRadius: '4px',
    fontSize: '0.75rem',
    textTransform: 'capitalize'
  },
  actionButton: {
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    border: '1px solid #3182ce',
    background: 'white',
    color: '#3182ce',
    fontSize: '0.75rem',
    cursor: 'pointer',
    marginRight: '0.5rem'
  },
  downloadButton: {
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    border: '1px solid #48bb78',
    background: '#48bb78',
    color: 'white',
    fontSize: '0.75rem',
    cursor: 'pointer'
  },
  emptyState: {
    padding: '2rem',
    textAlign: 'center',
    color: '#718096'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    background: 'white',
    borderRadius: '8px',
    padding: '1.5rem',
    minWidth: '500px',
    maxWidth: '600px',
    maxHeight: '80vh',
    overflow: 'auto',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
  },
  modalTitle: {
    margin: '0 0 1.5rem 0',
    fontSize: '1.25rem',
    fontWeight: '600'
  },
  formGroup: {
    marginBottom: '1rem'
  },
  label: {
    display: 'block',
    marginBottom: '0.25rem',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#4a5568'
  },
  input: {
    width: '100%',
    padding: '0.5rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    fontSize: '0.875rem'
  },
  modalActions: {
    display: 'flex',
    gap: '0.5rem',
    justifyContent: 'flex-end',
    marginTop: '1.5rem'
  },
  cancelButton: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    background: 'white',
    fontSize: '0.875rem',
    cursor: 'pointer'
  },
  submitButton: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #3182ce',
    background: '#3182ce',
    color: 'white',
    fontSize: '0.875rem',
    cursor: 'pointer'
  }
};
